import { ComponentProps, FC } from "react";
import { Alert } from "antd";
import { MailOutlined, EyeTwoTone, EyeInvisibleOutlined, UnlockOutlined } from "@ant-design/icons";
import { Form, Input } from "formik-antd";
import * as _ from "lodash";

import { classname } from "@ctra/utils";
import { AdvisorType } from "@ctra/api";

import { IdcardOutlined, PhoneOutlined, Select, Button } from "../../../index";
import styles from "./AcceptInviteForm.module.less";
import * as React from "react";

export interface AcceptInviteFormProps extends ComponentProps<typeof Form> {
  /**
   * translated labels
   */
  labels: {
    firstName: string;
    lastName: string;
    companyName: string;
    subType: string;
    emailAddress: string;
    phoneNumber: string;
    password: string;
    confirmPassword: string;
    submit: string;
    rejected?: string;
  };
  /**
   * Get sub type
   * @param {string} subType
   * @return {string}
   */
  getSubType: (subType: string) => string;
  /**
   * handle field blur
   * @param {string} fieldName
   */
  onFieldBlur: (
    fieldName:
      | "firstName"
      | "lastName"
      | "companyName"
      | "subType"
      | "emailAddress"
      | "phoneNumber"
      | "password"
      | "confirmPassword"
  ) => void;
  /**
   * loading state
   */
  isLoading?: boolean;
}

/**
 * Test ids for unit testing
 * @type {{firstName: string, lastName: string, password: string, emailAddress: string, phoneNumber: string, submit: string, companyName: string, confirmPassword: string, subType: string, error: string}}
 */
export const testIds = {
  firstName: "firstName",
  lastName: "lastName",
  companyName: "companyName",
  password: "password",
  confirmPassword: "confirmPassword",
  phoneNumber: "phoneNumber",
  subType: "subType",
  emailAddress: "emailAddress",
  submit: "submit",
  error: "error"
};

/**
 * Accept invite form component
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} companyName
 * @param {string} password
 * @param {string} confirmPassword
 * @param {string} phoneNumber
 * @param {string} subType
 * @param {string} emailAddress
 * @param {string} submit
 * @param {string | undefined} rejected
 * @param {boolean | undefined} isLoading
 * @param {(subType: string) => string} getSubType
 * @param {(fieldName: ("firstName" | "lastName" | "companyName" | "subType" | "emailAddress" | "phoneNumber" | "password" | "confirmPassword")) => void} onFieldBlur
 * @param {string | undefined} className
 * @param {Pick<AcceptInviteFormProps & {children?: React.ReactNode | undefined}, "prefixCls" | "colon" | "name" | "layout" | "labelAlign" | "labelWrap" | "labelCol" | "wrapperCol" | "form" | "size" | "disabled" | "scrollToFirstError" | "requiredMark" | "hideRequiredMark" | "initialValues" | "children" | "component" | "fields" | "validateMessages" | "onValuesChange" | "onFieldsChange" | "onFinish" | "onFinishFailed" | "validateTrigger" | "preserve" | "acceptCharset" | "action" | "autoComplete" | "encType" | "method" | "noValidate" | "target" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture">} rest
 * @return {JSX.Element}
 * @constructor
 */
const AcceptInviteForm: FC<AcceptInviteFormProps> & { testIds: typeof testIds } = ({
  labels: {
    firstName,
    lastName,
    companyName,
    password,
    confirmPassword,
    phoneNumber,
    subType,
    emailAddress,
    submit,
    rejected
  },
  isLoading,
  getSubType,
  onFieldBlur,
  className,
  ...rest
}) => (
  <Form
    size="large"
    className={classname("ctra-auth-AcceptInviteForm", styles.AcceptInviteForm, className)}
    {...rest}
  >
    <Form.Item name="firstName">
      <Input
        data-testid={testIds.firstName}
        disabled={isLoading}
        type="text"
        name="firstName"
        placeholder={firstName}
        onBlur={_.partial(onFieldBlur, "firstName")}
      />
    </Form.Item>
    <Form.Item name="lastName">
      <Input
        data-testid={lastName}
        disabled={isLoading}
        type="text"
        name="lastName"
        placeholder={lastName}
        onBlur={_.partial(onFieldBlur, "lastName")}
      />
    </Form.Item>
    <Form.Item name="companyName">
      <Input
        data-testid={testIds.companyName}
        disabled={isLoading}
        type="text"
        name="companyName"
        placeholder={companyName}
        prefix={<IdcardOutlined />}
        onBlur={_.partial(onFieldBlur, "companyName")}
      />
    </Form.Item>

    <Form.Item name="subType">
      <Select
        onBlur={_.partial(onFieldBlur, "subType")}
        name="subType"
        data-testid={testIds.subType}
        disabled={isLoading}
        placeholder={subType}
      >
        {Object.values(AdvisorType).map((value) => (
          <Select.Option key={value} value={value}>
            {getSubType(value)}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
    <Form.Item name="email">
      <Input
        data-testid={testIds.emailAddress}
        disabled={isLoading}
        type="email"
        name="email"
        placeholder={emailAddress}
        prefix={<MailOutlined />}
        onBlur={_.partial(onFieldBlur, "emailAddress")}
      />
    </Form.Item>
    <Form.Item name="phoneNumber">
      <Input
        data-testid={testIds.phoneNumber}
        disabled={isLoading}
        type="tel"
        name="phoneNumber"
        placeholder={phoneNumber}
        prefix={<PhoneOutlined />}
        onBlur={_.partial(onFieldBlur, "phoneNumber")}
      />
    </Form.Item>
    <Form.Item name="password">
      <Input.Password
        data-testid={testIds.password}
        disabled={isLoading}
        name="password"
        placeholder={password}
        iconRender={
          // istanbul ignore next
          (visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)
        }
        prefix={<UnlockOutlined />}
        onBlur={_.partial(onFieldBlur, "password")}
      />
    </Form.Item>
    <Form.Item name="confirmPassword">
      <Input.Password
        data-testid={testIds.confirmPassword}
        disabled={isLoading}
        name="confirmPassword"
        placeholder={confirmPassword}
        iconRender={
          // istanbul ignore next
          (visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)
        }
        prefix={<UnlockOutlined />}
        onBlur={_.partial(onFieldBlur, "confirmPassword")}
      />
    </Form.Item>
    <Button
      block
      type="primary"
      htmlType="submit"
      size="large"
      data-testid={testIds.submit}
      loading={isLoading}
      disabled={isLoading}
    >
      {submit}
    </Button>
    {rejected && (
      <Alert
        closable
        showIcon
        type="error"
        data-testid={testIds.error}
        message={rejected}
        className={styles.Extra}
      />
    )}
  </Form>
);

AcceptInviteForm.testIds = testIds;

export default AcceptInviteForm;
